import { useEffect } from 'react';
import './App.css';
import ImprovedEnterpriseLayoutPreview from './Layout/main_layout';
import LoginInterface from './login-interface';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { GlobalErrorHandler } from './components/common/GlobalErrorHandler';
import { SnackbarProvider } from './components/common/SnackbarNotification';
import { TabTakeoverGuard } from './components/common/TabTakeoverGuard';
import {
  clearAllAuthStorage,
  initCrossTabLogin,
  initCrossTabLogout,
  installBackNavigationGuard,
} from './utils/authGuard';
import { tokenUtils } from './api/config/apiConfig';

/**
 * Listens for logout broadcasts from other tabs (via BroadcastChannel /
 * `storage` event). When another tab logs out, wipe local state and bounce
 * this tab back to the login route so the whole app is signed out in sync.
 *
 * When we ARE on the login route, additionally trap the browser back button
 * so users can't navigate back into a cached authenticated page after logout.
 */
// Module-scoped flag: true while the authenticated shell (not the login view)
// is the mounted root. Lets the cross-tab LOGIN handler reload only tabs still
// sitting on the login screen, without disturbing an active app tab.
let appViewMounted = false;

/**
 * Root gate. The login screen and the authenticated shell are BOTH served at
 * the bare root `/`, so the address bar shows no path segment in either state.
 * Which one renders is decided purely by auth state, re-evaluated every render
 * (a post-login `navigate('/')` re-renders this component and flips the view).
 */
function RootGate() {
  const authed = tokenUtils.isAuthenticated();
  useEffect(() => {
    appViewMounted = authed;
  }, [authed]);
  return authed ? <ImprovedEnterpriseLayoutPreview /> : <LoginInterface />;
}

/**
 * Cross-tab auth sync + login-view back-button trap. Login and app both live
 * at `/`, so the current state is read from the auth token, not the path.
 * `useLocation()` keeps this gate re-rendering across in-app navigations.
 */
function AuthLifecycle() {
  const location = useLocation();
  const authed = tokenUtils.isAuthenticated();

  useEffect(() => {
    return initCrossTabLogout(() => {
      clearAllAuthStorage();
      // Reload the root so this tab drops back to the login view.
      window.location.replace('/');
    });
  }, []);

  useEffect(() => {
    return initCrossTabLogin(() => {
      // Only reload tabs still sitting on the login screen — don't disturb an
      // actively-used authenticated tab.
      if (!appViewMounted) window.location.replace('/');
    });
  }, []);

  useEffect(() => {
    // Trap the browser Back button while on the login view (e.g. post-logout)
    // so a cached authenticated page can't be reached through history.
    if (authed) return;
    return installBackNavigationGuard();
  }, [authed, location.key]);

  return null;
}

/**
 * Mounts the single-tab presence guard only once authenticated. Pre-auth
 * double-tabs coexist freely (helpful when someone opens the URL twice before
 * signing in); enforcement kicks in the moment the shell renders.
 */
function TabPresenceGate() {
  useLocation(); // subscribe to navigation so auth transitions re-render this gate
  if (!tokenUtils.isAuthenticated()) return null;
  return <TabTakeoverGuard />;
}

function App() {
  return (
    <GlobalErrorHandler>
      <ErrorBoundary
        showNotification={true}
        logError={true}
        errorMessage="The application encountered an error. Please try again."
      >
        <SnackbarProvider>
          <AuthLifecycle />
          <TabPresenceGate />
          <Routes>
            {/* Login screen AND authenticated shell are both served at the bare
                root — no path segment in the address bar in either state.
                RootGate picks which one to render based on auth. */}
            <Route path="/" element={<RootGate />} />
            {/* Back-compat: any old explicit path collapses to the clean root. */}
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/dashboard" element={<Navigate to="/" replace />} />
            <Route path="/Layout/main_layout" element={<Navigate to="/" replace />} />
            {/* other routes */}
          </Routes>
        </SnackbarProvider>
      </ErrorBoundary>
    </GlobalErrorHandler>
  );
}

export default App;
